// One ping/pong exchange, NTP style: sent by the client at t0, seen by the server at t1, answered
// at t2, back at the client at t3. t0 and t3 are the client's clock, t1 and t2 the server's.
export type Sample = {
  readonly t0: number;
  readonly t1: number;
  readonly t2: number;
  readonly t3: number;
};

// What one exchange says: `offset` is server clock minus client clock, `rtt` the time the
// exchange spent on the wire.
export type Reading = {
  readonly offset: number;
  readonly rtt: number;
};

export const read = (sample: Sample): Reading => ({
  offset: (sample.t1 - sample.t0 + (sample.t2 - sample.t3)) / 2,
  rtt: sample.t3 - sample.t0 - (sample.t2 - sample.t1),
});

// Sixteen readings: the eight of the connect burst plus the last sixteen seconds of steady pings,
// enough for the median below to shrug off a slow exchange or two.
export const WINDOW = 16;

export const remember = (
  readings: ReadonlyArray<Reading>,
  reading: Reading,
): ReadonlyArray<Reading> => [...readings, reading].slice(-WINDOW);

// `samples` is the number of readings the estimate stands on; 0 means nothing is known yet and
// the offset is only the default.
export type Estimate = {
  readonly offset: number;
  readonly rtt: number;
  readonly samples: number;
};

// The offset the readings agree on: the median offset of the half with the shortest round trips
// (a short trip leaves the least room for one leg to be slower than the other), and the shortest
// round trip seen. A reading with a negative round trip was taken across a client clock step and
// says nothing about the server.
export const estimate = (readings: ReadonlyArray<Reading>): Estimate => {
  const usable = readings.filter((reading) => reading.rtt >= 0).sort((a, b) => a.rtt - b.rtt);
  if (usable.length === 0) {
    return { offset: 0, rtt: 0, samples: 0 };
  }
  const best = usable.slice(0, Math.ceil(usable.length / 2));
  const offsets = best.map((reading) => reading.offset).sort((a, b) => a - b);
  const middle = Math.floor(offsets.length / 2);
  const offset =
    offsets.length % 2 === 1 ? offsets[middle] : (offsets[middle - 1] + offsets[middle]) / 2;
  return { offset, rtt: usable[0].rtt, samples: usable.length };
};
